import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import myAxios from '../AxiosUrl';
import styles from './SEOManagement.module.css';
import { AiOutlineSearch, AiOutlinePlus, AiOutlineEdit, AiOutlineUnorderedList, AiOutlineAppstore } from 'react-icons/ai';
import { FiPackage } from 'react-icons/fi';

function FAQManagement() {
    const [faqs, setFaqs] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showFAQForm, setShowFAQForm] = useState(false);
    const [selectedProductId, setSelectedProductId] = useState(null);
    const [faqList, setFaqList] = useState([
        { question: '', answer: '', category: 'general', isActive: true }
    ]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalProducts, setTotalProducts] = useState(0);
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'

    const uid = localStorage.getItem('id');

    useEffect(() => {
        fetchFAQs();
    }, [currentPage, itemsPerPage, searchTerm]);

    const fetchFAQs = async () => {
        setLoading(true);
        try {
            const response = await myAxios.get(`/seo/faqs/${uid}`, {
                params: {
                    page: currentPage,
                    limit: itemsPerPage,
                    search: searchTerm
                }
            });
            if (response.data.success) {
                setFaqs(response.data.data);
                // Get products from the same response
                if (response.data.products) {
                    setProducts(response.data.products);
                }
                // Set pagination metadata
                if (response.data.pagination) {
                    setTotalPages(response.data.pagination.totalPages);
                    setTotalProducts(response.data.pagination.totalProducts);
                }
            }
        } catch (error) {
            console.error('Error fetching FAQs:', error);
            toast.error('Failed to fetch FAQs');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Filter valid FAQs (with both question and answer)
            const validFaqs = faqList.filter(faq => faq.question.trim() && faq.answer.trim());

            if (validFaqs.length === 0) {
                toast.warning('Please add at least one FAQ with both question and answer');
                setLoading(false);
                return;
            }

            // Separate existing FAQs (with _id) from new FAQs (without _id)
            const existingFaqs = validFaqs.filter(faq => faq._id);
            const newFaqs = validFaqs.filter(faq => !faq._id);

            // Update existing FAQs
            for (const faq of existingFaqs) {
                await myAxios.put(`/seo/faq/${uid}/${selectedProductId}/${faq._id}`, {
                    question: faq.question,
                    answer: faq.answer
                });
            }

            // Create new FAQs
            for (const faq of newFaqs) {
                await myAxios.post(`/seo/faq/${uid}`, {
                    question: faq.question,
                    answer: faq.answer,
                    productIds: [selectedProductId]
                });
            }

            const totalUpdated = existingFaqs.length + newFaqs.length;
            toast.success(`${totalUpdated} FAQ(s) saved successfully!`);
            fetchFAQs();
            handleCloseModal();
        } catch (error) {
            console.error('Error saving FAQ:', error);
            toast.error('Failed to save FAQ');
        } finally {
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowFAQForm(false);
        setSelectedProductId(null);
        setFaqList([{ question: '', answer: '', category: 'general', isActive: true }]);
    };

    const handleAddFAQ = () => {
        setFaqList([...faqList, { question: '', answer: '', category: 'general', isActive: true }]);
    };

    const handleRemoveFAQ = (index) => {
        if (faqList.length === 1) {
            toast.warning('At least one FAQ is required');
            return;
        }
        setFaqList(faqList.filter((_, i) => i !== index));
    };

    const handleFAQChange = (index, field, value) => {
        const updatedList = [...faqList];
        updatedList[index][field] = value;
        setFaqList(updatedList);
    };

    const handleManageFAQs = (product) => {
        setSelectedProductId(product._id);

        // Get FAQs for this product
        const productFAQs = faqs.filter(faq => faq.productId === product._id);

        if (productFAQs.length > 0) {
            // Load existing FAQs
            setFaqList(productFAQs.map(faq => ({
                question: faq.question,
                answer: faq.answer,
                category: faq.category || 'general',
                isActive: faq.isActive !== undefined ? faq.isActive : true,
                _id: faq._id
            })));
        } else {
            // Start with empty FAQ for new product
            setFaqList([{ question: '', answer: '', category: 'general', isActive: true }]);
        }

        setShowFAQForm(true);
    };

    // Calculate display indexes for pagination info
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + products.length;

    // Reset to page 1 when search term or items per page changes
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setCurrentPage(1);
        }, 500); // Debounce search
        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleItemsPerPageChange = (newLimit) => {
        setItemsPerPage(newLimit);
        setCurrentPage(1);
    };

    // Helper function to check if product has FAQs
    const productHasFAQs = (productId) => {
        return faqs.some(faq => faq.productId === productId);
    };

    // Helper function to get FAQ count for a product
    const getProductFAQCount = (productId) => {
        return faqs.filter(faq => faq.productId === productId).length;
    };

    // Helper function to get product image based on product type
    const getProductImage = (product) => {
        // Priority 1: New images array (with url property)
        if (product.images && product.images[0] && product.images[0].url) {
            return product.images[0].url;
        }

        // Priority 2: Check variation data for color/colorSize products
        if (product.variation === 'color' && product.variationData && product.variationData[0]) {
            if (product.variationData[0].url) {
                return product.variationData[0].url;
            }
        }

        if (product.variation === 'colorSize' && product.variationData && product.variationData[0]) {
            if (product.variationData[0].url) {
                return product.variationData[0].url;
            }
        }

        // Priority 3: Old image field (imgeUrl - note the typo in DB)
        if (product.imgeUrl) {
            return product.imgeUrl;
        }

        // Priority 4: imageUrl field
        if (product.imageUrl) {
            return product.imageUrl;
        }

        // Default placeholder
        return 'https://via.placeholder.com/200';
    };

    return (
        <div className={styles.container}>
            <ToastContainer position="top-right" autoClose={3000} />

            {/* Combined Header Section */}
            <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                marginBottom: '24px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
                {/* Title and Description */}
                <div style={{ marginBottom: '20px' }}>
                    <h1 style={{
                        fontSize: '28px',
                        fontWeight: '600',
                        color: '#202223',
                        margin: '0 0 8px 0'
                    }}>
                        FAQ Management
                    </h1>
                    <p style={{
                        fontSize: '14px',
                        color: '#6d7175',
                        margin: '0'
                    }}>
                        Manage frequently asked questions for SEO optimization
                    </p>
                </div>

                {/* Search and View Toggle Row */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '16px',
                    flexWrap: 'wrap'
                }}>
                    <div className={styles.searchBox}>
                        <AiOutlineSearch />
                        <input
                            type="text"
                            placeholder="Search products..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* View Toggle Buttons */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                            onClick={() => setViewMode('list')}
                            style={{
                                padding: '10px 16px',
                                backgroundColor: viewMode === 'list' ? '#008060' : 'white',
                                color: viewMode === 'list' ? 'white' : '#202223',
                                border: `1px solid ${viewMode === 'list' ? '#008060' : '#c9cccf'}`,
                                borderRadius: '8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '14px',
                                fontWeight: '500',
                                transition: 'all 0.2s ease',
                                outline: 'none'
                            }}
                        >
                            <AiOutlineUnorderedList size={18} />
                            List
                        </button>
                        <button
                            onClick={() => setViewMode('grid')}
                            style={{
                                padding: '10px 16px',
                                backgroundColor: viewMode === 'grid' ? '#008060' : 'white',
                                color: viewMode === 'grid' ? 'white' : '#202223',
                                border: `1px solid ${viewMode === 'grid' ? '#008060' : '#c9cccf'}`,
                                borderRadius: '8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '14px',
                                fontWeight: '500',
                                transition: 'all 0.2s ease',
                                outline: 'none'
                            }}
                        >
                            <AiOutlineAppstore size={18} />
                            Grid
                        </button>
                    </div>
                </div>
            </div>

            {loading && (
                <div>
                    {/* Results Info Skeleton */}
                    <div className={styles.skeletonContainer} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', marginBottom: '16px' }}>
                        <div className={styles.skeleton} style={{ width: '200px', height: '14px' }}></div>
                        <div className={styles.skeleton} style={{ width: '120px', height: '36px', borderRadius: '6px' }}></div>
                    </div>

                    {/* Product List Skeleton */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {[1, 2, 3, 4, 5, 6].map((item) => (
                            <div key={item} className={styles.skeletonContainer} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', marginBottom: '0' }}>
                                {/* Product Image Skeleton */}
                                <div className={styles.skeleton} style={{ width: '80px', height: '80px', borderRadius: '8px', flexShrink: 0 }}></div>
                                {/* Product Info Skeleton */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className={styles.skeleton} style={{ width: `${60 + (item * 5) % 30}%`, height: '16px', marginBottom: '8px' }}></div>
                                    <div className={styles.skeleton} style={{ width: '60px', height: '13px' }}></div>
                                </div>
                                {/* Action Button Skeleton */}
                                <div className={styles.skeleton} style={{ width: '130px', height: '38px', borderRadius: '8px', flexShrink: 0 }}></div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Showing results info */}
            {totalProducts > 0 && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                    padding: '12px 16px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e1e3e5'
                }}>
                    <span style={{fontSize: '14px', color: '#6d7175'}}>
                        Showing {startIndex + 1}-{Math.min(endIndex, totalProducts)} of {totalProducts} products
                    </span>
                    <select
                        value={itemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                        style={{
                            padding: '6px 12px',
                            border: '1px solid #c9cccf',
                            borderRadius: '6px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            outline: 'none'
                        }}
                    >
                        <option value={10}>10 per page</option>
                        <option value={20}>20 per page</option>
                        <option value={50}>50 per page</option>
                        <option value={100}>100 per page</option>
                    </select>
                </div>
            )}

            {/* Product Display - Grid or List View */}
            {viewMode === 'grid' ? (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: '20px'
                }}>
                    {!loading && products.length === 0 ? (
                        <div style={{
                            gridColumn: '1 / -1',
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '60px 20px',
                            textAlign: 'center',
                            color: '#6d7175'
                        }}>
                            <div style={{fontSize: '48px', marginBottom: '16px'}}><FiPackage /></div>
                            <h3 style={{fontSize: '18px', fontWeight: '600', color: '#202223', margin: '0 0 8px 0'}}>
                                No products found
                            </h3>
                            <p style={{fontSize: '14px', margin: '0'}}>
                                {searchTerm ? 'Try a different search term' : 'Add products to manage their FAQs'}
                            </p>
                        </div>
                    ) : (
                        products.map((product) => {
                            const hasFAQs = productHasFAQs(product._id);
                            const faqCount = getProductFAQCount(product._id);

                            return (
                                <div key={product._id} style={{
                                    backgroundColor: 'white',
                                    borderRadius: '12px',
                                    border: '1px solid #e1e3e5',
                                    overflow: 'hidden',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}>
                                {/* Product Image */}
                                <div style={{
                                    width: '100%',
                                    height: '200px',
                                    overflow: 'hidden',
                                    backgroundColor: '#f6f6f7'
                                }}>
                                    <img
                                        src={getProductImage(product)}
                                        alt={product.productName}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </div>

                                {/* Product Info */}
                                <div style={{
                                    padding: '16px',
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}>
                                    <h3 style={{
                                        margin: '0 0 8px 0',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        color: '#202223',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        minHeight: '40px'
                                    }}>
                                        {product.productName}
                                    </h3>

                                    {hasFAQs && (
                                        <p style={{
                                            margin: '0 0 12px 0',
                                            fontSize: '13px',
                                            color: '#6d7175'
                                        }}>
                                            {faqCount} FAQ{faqCount > 1 ? 's' : ''}
                                        </p>
                                    )}

                                    <button
                                        onClick={() => handleManageFAQs(product)}
                                        style={{
                                            marginTop: 'auto',
                                            padding: '10px 16px',
                                            backgroundColor: hasFAQs ? '#008060' : '#2c6ecb',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '6px',
                                            transition: 'all 0.2s ease',
                                            outline: 'none'
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = hasFAQs
                                                ? '0 4px 12px rgba(0, 128, 96, 0.3)'
                                                : '0 4px 12px rgba(44, 110, 203, 0.3)';
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}
                                    >
                                        {hasFAQs ? (
                                            <>
                                                <AiOutlineEdit /> Manage FAQs
                                            </>
                                        ) : (
                                            <>
                                                <AiOutlinePlus /> Add FAQ
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )}
                </div>
            ) : (
                /* List View */
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {!loading && products.length === 0 ? (
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '60px 20px',
                            textAlign: 'center',
                            color: '#6d7175'
                        }}>
                            <div style={{fontSize: '48px', marginBottom: '16px'}}><FiPackage /></div>
                            <h3 style={{fontSize: '18px', fontWeight: '600', color: '#202223', margin: '0 0 8px 0'}}>
                                No products found
                            </h3>
                            <p style={{fontSize: '14px', margin: '0'}}>
                                {searchTerm ? 'Try a different search term' : 'Add products to manage their FAQs'}
                            </p>
                        </div>
                    ) : (
                        products.map((product) => {
                            const hasFAQs = productHasFAQs(product._id);
                            const faqCount = getProductFAQCount(product._id);

                            return (
                                <div key={product._id} style={{
                                    backgroundColor: 'white',
                                    borderRadius: '12px',
                                    border: '1px solid #e1e3e5',
                                    padding: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '16px',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                                >
                                    {/* Product Image - Smaller in list view */}
                                    <div style={{
                                        width: '80px',
                                        height: '80px',
                                        flexShrink: 0,
                                        overflow: 'hidden',
                                        borderRadius: '8px',
                                        backgroundColor: '#f6f6f7'
                                    }}>
                                        <img
                                            src={getProductImage(product)}
                                            alt={product.productName}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    </div>

                                    {/* Product Info */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <h3 style={{
                                            fontSize: '16px',
                                            fontWeight: '600',
                                            color: '#202223',
                                            margin: '0 0 4px 0',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {product.productName}
                                        </h3>
                                        {hasFAQs && (
                                            <p style={{
                                                margin: '0',
                                                fontSize: '13px',
                                                color: '#6d7175'
                                            }}>
                                                {faqCount} FAQ{faqCount > 1 ? 's' : ''}
                                            </p>
                                        )}
                                    </div>

                                    {/* Action Button */}
                                    <button
                                        onClick={() => handleManageFAQs(product)}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: hasFAQs ? '#008060' : '#2c6ecb',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            whiteSpace: 'nowrap',
                                            transition: 'all 0.2s ease',
                                            outline: 'none'
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-1px)';
                                            e.currentTarget.style.boxShadow = hasFAQs
                                                ? '0 2px 8px rgba(0, 128, 96, 0.3)'
                                                : '0 2px 8px rgba(44, 110, 203, 0.3)';
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}
                                    >
                                        {hasFAQs ? (
                                            <>
                                                <AiOutlineEdit /> Manage FAQs
                                            </>
                                        ) : (
                                            <>
                                                <AiOutlinePlus /> Add FAQ
                                            </>
                                        )}
                                    </button>
                                </div>
                            );
                        })
                    )}
                </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '24px',
                    padding: '16px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e1e3e5'
                }}>
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        style={{
                            padding: '8px 16px',
                            border: '1px solid #c9cccf',
                            borderRadius: '6px',
                            backgroundColor: currentPage === 1 ? '#f6f6f7' : 'white',
                            color: currentPage === 1 ? '#9ca3af' : '#202223',
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            outline: 'none'
                        }}
                    >
                        Previous
                    </button>

                    <div style={{display: 'flex', gap: '4px'}}>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                            // Show first page, last page, current page, and pages around current
                            const showPage =
                                page === 1 ||
                                page === totalPages ||
                                (page >= currentPage - 1 && page <= currentPage + 1);

                            const showEllipsis =
                                (page === currentPage - 2 && currentPage > 3) ||
                                (page === currentPage + 2 && currentPage < totalPages - 2);

                            if (showEllipsis) {
                                return <span key={page} style={{padding: '8px 4px', color: '#6d7175'}}>...</span>;
                            }

                            if (!showPage) return null;

                            return (
                                <button
                                    key={page}
                                    onClick={() => handlePageChange(page)}
                                    style={{
                                        padding: '8px 12px',
                                        border: '1px solid #c9cccf',
                                        borderRadius: '6px',
                                        backgroundColor: currentPage === page ? '#008060' : 'white',
                                        color: currentPage === page ? 'white' : '#202223',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        minWidth: '40px',
                                        outline: 'none'
                                    }}
                                >
                                    {page}
                                </button>
                            );
                        })}
                    </div>

                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        style={{
                            padding: '8px 16px',
                            border: '1px solid #c9cccf',
                            borderRadius: '6px',
                            backgroundColor: currentPage === totalPages ? '#f6f6f7' : 'white',
                            color: currentPage === totalPages ? '#9ca3af' : '#202223',
                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            outline: 'none'
                        }}
                    >
                        Next
                    </button>
                </div>
            )}

            {/* FAQ Form Modal */}
            {showFAQForm && (
                <div className={styles.modalOverlay} onClick={handleCloseModal} style={{backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 9999}}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2>Manage FAQs</h2>
                            <button className={styles.closeBtn} onClick={handleCloseModal}>&times;</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            {selectedProductId && (
                                <div style={{padding: '12px 20px', backgroundColor: '#f0fdf4', borderBottom: '1px solid #c9cccf', fontSize: '14px', color: '#008060'}}>
                                    Managing FAQ(s) for: {products.find(p => p._id === selectedProductId)?.productName}
                                </div>
                            )}

                            <div style={{padding: '20px', maxHeight: '60vh', overflowY: 'auto'}} className={styles.hideScrollbarFAQ}>
                                {faqList.map((faq, index) => (
                                    <div key={index} style={{
                                        marginBottom: '24px',
                                        padding: '16px',
                                        border: '1px solid #e1e3e5',
                                        borderRadius: '8px',
                                        backgroundColor: '#f9fafb'
                                    }}>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
                                            <h4 style={{margin: 0, fontSize: '16px', color: '#202223'}}>FAQ #{index + 1}</h4>
                                            {faqList.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveFAQ(index)}
                                                    style={{
                                                        background: '#ffebee',
                                                        color: '#d32f2f',
                                                        border: 'none',
                                                        padding: '6px 12px',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label>Question *</label>
                                            <input
                                                type="text"
                                                value={faq.question}
                                                onChange={(e) => handleFAQChange(index, 'question', e.target.value)}
                                                required
                                                maxLength={200}
                                                placeholder="Enter the question..."
                                            />
                                            <small>{faq.question.length}/200 characters</small>
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label>Answer *</label>
                                            <textarea
                                                value={faq.answer}
                                                onChange={(e) => handleFAQChange(index, 'answer', e.target.value)}
                                                required
                                                maxLength={1000}
                                                rows={4}
                                                placeholder="Enter the answer..."
                                            />
                                            <small>{faq.answer.length}/1000 characters</small>
                                        </div>
                                    </div>
                                ))}

                                <button
                                    type="button"
                                    onClick={handleAddFAQ}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        border: '2px dashed #008060',
                                        background: 'white',
                                        color: '#008060',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}
                                >
                                    + Add Another FAQ
                                </button>
                            </div>

                            <div className={styles.modalActions}>
                                <button type="button" className={styles.cancelBtn} onClick={handleCloseModal}>
                                    Cancel
                                </button>
                                <button type="submit" className={styles.saveBtn} disabled={loading}>
                                    {loading ? 'Saving...' : `Save ${faqList.length} FAQ(s)`}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default FAQManagement;
